// Loyalty-pass (FlowPass) integration for the popup renderer.
//
// This file renders the pass VISUAL in the popup's image slot, using the same
// WalletPassPreview engine as the flowIQ admin dashboard, driven by
// PassPreviewData from api/popup-settings.ts (or the builder preview bridge).
// The pass BUTTON is not here: the popup's own themed button doubles as it,
// in PopupRenderer, so a pass popup never grows a second button.
//
// The serial comes from the SERVER, via api/popup-pass — this file does not
// compute it. It used to: a `derivePassSerial` here mirrored the edge
// function's SHA-256 of `${popupId}:${phone}` so the widget could build the
// pass URL with no round-trip. That mirror was removed on 27 Aug 2026 because
// two independent implementations of one identity rule cannot be kept honest:
// the day the edge function started reusing a returning member's existing card
// instead of minting a fresh one, the browser's guess pointed at a pass that
// was never theirs, and the customer got a dead link.
//
// It was also the weaker security model. A derivable serial can be computed
// offline by anyone holding a popup id and a phone number, with no submission
// and no time limit, and a serial is a capability — it opens the card and
// authorises redeeming points. The read-back endpoint is scoped to the browser
// that just submitted, checked against the org, and expires.

import type { IssuedPass } from "../../lib/popup-api";
import { WalletPassPreview } from "./wallet/WalletPassPreview";
import type { PassPreviewData, PassFieldMapping } from "@/shared/types/popup-types";

/** Phone normalization for the PREVIEW's phone field — byte-identical to the
 *  edge fn / broadcast.js. This is display only; it no longer feeds any serial
 *  derivation (that left with the mirror on 27 Aug 2026). */
export function normalizePassPhone(phone: string): string {
  if (!phone) return "";
  let normalized = phone.replace(/\D/g, "");
  if (normalized.startsWith("0")) normalized = "27" + normalized.substring(1);
  if (normalized.length === 9 && !normalized.startsWith("0")) normalized = "27" + normalized;
  return normalized;
}

/** Resolve the template's dynamic-field values from the visitor's (partial)
 *  form data — the same mapping semantics as the edge fn, plus friendly
 *  placeholders so the pass looks real before anything is typed. */
export function resolvePassValues(
  mappings: Record<string, PassFieldMapping>,
  form: { name?: string; email?: string; whatsapp_number?: string },
  serialText: string
): Record<string, string | number> {
  const values: Record<string, string | number> = {};
  for (const [key, mapping] of Object.entries(mappings || {})) {
    switch (mapping.source) {
      case "name":
        values[key] = form.name?.trim() || "Your Name";
        break;
      case "first_name":
        values[key] = form.name?.trim().split(" ")[0] || "You";
        break;
      case "email":
        if (form.email?.trim()) values[key] = form.email.trim();
        break;
      case "whatsapp_number": {
        const n = normalizePassPhone(form.whatsapp_number || "");
        if (n) values[key] = n;
        break;
      }
      case "serial":
        values[key] = serialText;
        break;
      case "static":
        if (mapping.value !== undefined && mapping.value !== "") values[key] = mapping.value;
        break;
    }
  }
  return values;
}

/** The pass rendered in the popup's visual slot — same engine + artwork as
 *  the admin dashboard, on a quiet dark stage so any template pops. */
export function PassShowcase({
  passPreview,
  form,
  issuedPass,
}: {
  passPreview: PassPreviewData;
  form: { name?: string; email?: string; whatsapp_number?: string };
  /** Once the server has answered, the card's REAL standing. */
  issuedPass?: IssuedPass | null;
}) {
  const serialText = issuedPass?.serial
    ? issuedPass.serial
    : `${(passPreview.serial_prefix || "PASS").toUpperCase()}-XXXXXXXXXX`;
  const values = resolvePassValues(passPreview.field_mappings, form, serialText);

  // Before the pass exists these keys fall through to the template's defaults —
  // 0 points, Bronze, 0 orders — which is honest for somebody brand new and a
  // LIE to a returning member, who was shown an empty card while holding a real
  // balance. Once the server tells us what the card actually says, say that.
  if (issuedPass) {
    if (issuedPass.points !== null) values.points = issuedPass.points;
    if (issuedPass.tier) values.tier = issuedPass.tier;
    if (issuedPass.orders !== null) values.orders = issuedPass.orders;
    // Their stored name beats what they typed: it is what the card is issued to.
    if (issuedPass.member_name) values.member = issuedPass.member_name;
  }
  // Configurable stage colour (pass_config.stage_background via the flowIQ
  // editor); empty = the default dark gradient.
  const stage = passPreview.stage_background?.trim();

  return (
    <div
      className={
        "flex h-full w-full items-center justify-center p-5 md:p-7" +
        (stage ? "" : " bg-gradient-to-b from-slate-900 to-slate-800")
      }
      style={stage ? { background: stage } : undefined}
    >
      <div className="w-[240px] max-w-full">
        <WalletPassPreview
          passJson={passPreview.pass_json}
          images={passPreview.images}
          values={values}
          width={240}
        />
      </div>
    </div>
  );
}
